// mu — `mu link pi`: install the mu pi extension and the mu skill.
//
// Modelled on murmur's `link pi` (~/hacking/crew/murmur/src/cli/link.ts):
// the extension is installed as a re-export SHIM, not a copy, so
// upgrading mu upgrades the extension with no relink. The skill is a
// SYMLINK to the package's `skills/mu`, for the same reason.
//
// Destinations live under `MU_PI_HOME ?? homedir()`; tests point
// MU_PI_HOME (or the `home` option) at a temp dir. Never writes into
// pi-meta's PI_CODING_AGENT_DIR instance dirs: pi-meta copies/links from
// ~/.pi/agent and ~/.agents at launch.

import {
  existsSync,
  lstatSync,
  mkdirSync,
  readFileSync,
  readlinkSync,
  realpathSync,
  rmSync,
  symlinkSync,
  writeFileSync,
} from "node:fs";
import { homedir } from "node:os";
import { dirname, join, resolve } from "node:path";
import { fileURLToPath, pathToFileURL } from "node:url";
import type { HasNextSteps, NextStep } from "./output.js";

/**
 * Identifies a generated shim on its own line, so re-linking (and
 * `inspectLinks`) can tell it from an older inlined copy without
 * depending on the import statement's exact shape.
 */
export const MU_SHIM_MARKER = "// mu:shim";

export type LinkState = "ok" | "missing" | "stale-copy" | "foreign" | "dangling";

export interface LinkStatus {
  path: string;
  state: LinkState;
  target?: string;
}

export interface LinkOptions {
  /** Root under which `.pi/` and `.agents/` live. Default `MU_PI_HOME ?? homedir()`. */
  home?: string;
}

/** Raised when the destination holds something mu did not put there
 *  (a real directory, or a symlink to another checkout without
 *  `--force`). mu never deletes user files to make room. */
export class LinkConflictError extends Error implements HasNextSteps {
  constructor(
    readonly path: string,
    readonly reason: "not-a-symlink" | "foreign-symlink",
    readonly current?: string,
  ) {
    super(
      reason === "not-a-symlink"
        ? `${path} exists and is not a symlink; refusing to replace it`
        : `${path} already links to ${current ?? "?"}; pass --force to replace it`,
    );
    this.name = "LinkConflictError";
  }

  errorNextSteps(): NextStep[] {
    if (this.reason === "foreign-symlink") {
      return [{ intent: "Replace the existing link", command: "mu link pi --force" }];
    }
    return [
      { intent: "Inspect what is there", command: `ls -la ${this.path}` },
      { intent: "Move it aside, then relink", command: `mv ${this.path} ${this.path}.bak` },
    ];
  }
}

function resolveHome(opts?: LinkOptions): string {
  return opts?.home ?? process.env.MU_PI_HOME ?? homedir();
}

/** Package root. src/link.ts and the bundled dist/*.js both sit exactly
 *  one directory below it. */
function packageRoot(): string {
  return resolve(dirname(fileURLToPath(import.meta.url)), "..");
}

/** The built extension the shim imports. `MU_EXTENSION_ENTRY` overrides
 *  it (tests point it at a fixture so they need no prior build). */
export function extensionEntry(): string {
  return process.env.MU_EXTENSION_ENTRY ?? join(packageRoot(), "dist", "extension", "mu-pi.js");
}

/** The skill directory the symlink points at. */
export function skillSource(): string {
  return join(packageRoot(), "skills", "mu");
}

export function extensionPath(opts?: LinkOptions): string {
  return join(resolveHome(opts), ".pi", "agent", "extensions", "mu.ts");
}

export function skillPath(opts?: LinkOptions): string {
  return join(resolveHome(opts), ".agents", "skills", "mu");
}

/**
 * The installed extension as a re-export of this installation. One
 * statement on purpose: any logic here is logic that cannot be upgraded.
 * A dynamic import, not `export ... from`, matching murmur's shim (ESM
 * hoisting bit it once).
 */
function shim(entry: string): string {
  return `${MU_SHIM_MARKER}
// Generated by \`mu link pi\`. Do not edit.
//
// A re-export, not a copy: the extension code lives in the mu install, so
// upgrading mu upgrades the extension. Re-run \`mu link pi\` only if the
// install path itself moves.
const { default: extension } = await import(${JSON.stringify(pathToFileURL(entry).href)});
export default extension;
`;
}

/** Install the extension shim (or, with `copy`, an inlined copy pinned
 *  to this version). `replacedCopy` is true when an existing file without
 *  the shim marker was overwritten. A symlink at the destination (e.g. to
 *  a dev checkout) is never written through: a live one needs `force`,
 *  which replaces the link itself, never its target. */
export function linkPi(opts?: LinkOptions & { copy?: boolean; force?: boolean }): {
  path: string;
  replacedCopy: boolean;
} {
  const path = extensionPath(opts);
  const entry = extensionEntry();
  if (!existsSync(entry)) {
    throw new Error(`mu pi extension not built: ${entry} is missing (run npm run build)`);
  }
  if (lstatOrUndefined(path)?.isSymbolicLink() === true) {
    const current = resolve(dirname(path), readlinkSync(path));
    if (existsSync(current) && opts?.force !== true) {
      throw new LinkConflictError(path, "foreign-symlink", current);
    }
    rmSync(path);
  }
  let replacedCopy = false;
  try {
    replacedCopy = !readFileSync(path, "utf8").includes(MU_SHIM_MARKER);
  } catch {
    // No previous install.
  }
  mkdirSync(dirname(path), { recursive: true });
  writeFileSync(path, opts?.copy === true ? readFileSync(entry, "utf8") : shim(entry));
  return { path, replacedCopy };
}

/** Symlink `<home>/.agents/skills/mu` to the package's `skills/mu`.
 *  `previous` is the old link target when one was replaced. */
export function linkSkill(opts?: LinkOptions & { force?: boolean }): {
  path: string;
  target: string;
  previous?: string;
} {
  const path = skillPath(opts);
  const target = skillSource();
  let previous: string | undefined;
  const st = lstatOrUndefined(path);
  if (st) {
    if (!st.isSymbolicLink()) throw new LinkConflictError(path, "not-a-symlink");
    const current = resolve(dirname(path), readlinkSync(path));
    if (current === target) return { path, target };
    // A dangling link holds nothing worth protecting; anything live
    // (e.g. a dev checkout) is replaced only on request.
    if (existsSync(current) && opts?.force !== true) {
      throw new LinkConflictError(path, "foreign-symlink", current);
    }
    previous = current;
    rmSync(path);
  }
  mkdirSync(dirname(path), { recursive: true });
  symlinkSync(target, path, "dir");
  return previous === undefined ? { path, target } : { path, target, previous };
}

function lstatOrUndefined(path: string): ReturnType<typeof lstatSync> | undefined {
  try {
    return lstatSync(path);
  } catch {
    return undefined;
  }
}

function inspectExtension(path: string): LinkStatus {
  let content: string;
  try {
    content = readFileSync(path, "utf8");
  } catch {
    return { path, state: "missing" };
  }
  if (!content.includes(MU_SHIM_MARKER)) return { path, state: "stale-copy" };
  const m = /await import\((".*?")\)/.exec(content);
  const spec = m?.[1];
  if (!spec) return { path, state: "dangling" };
  const raw = JSON.parse(spec) as string;
  const target = raw.startsWith("file:") ? fileURLToPath(raw) : raw;
  return { path, state: existsSync(target) ? "ok" : "dangling", target };
}

function inspectSkill(path: string): LinkStatus {
  const st = lstatOrUndefined(path);
  if (!st) return { path, state: "missing" };
  if (!st.isSymbolicLink()) return { path, state: "foreign" };
  let target: string;
  try {
    target = realpathSync(path);
  } catch {
    return { path, state: "dangling", target: resolve(dirname(path), readlinkSync(path)) };
  }
  let skillMd: string;
  try {
    skillMd = readFileSync(join(target, "SKILL.md"), "utf8");
  } catch {
    return { path, state: "foreign", target };
  }
  const front = /^---\n([\s\S]*?)\n---/.exec(skillMd)?.[1] ?? "";
  const isMu = /^name:\s*["']?mu["']?\s*$/m.test(front);
  return { path, state: isMu ? "ok" : "foreign", target };
}

/** Report the state of both installs without changing anything. */
export function inspectLinks(opts?: LinkOptions): { extension: LinkStatus; skill: LinkStatus } {
  return {
    extension: inspectExtension(extensionPath(opts)),
    skill: inspectSkill(skillPath(opts)),
  };
}
